import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { getPlayerToken } from '../cookie';
import { EditorBoard } from './EditorBoard';
import {
  COLOR_LABELS,
  DEFAULT_CAP,
  MAX_SIZE,
  MIN_SIZE,
  blankRegions,
  cloneRegions,
  countSolutions,
  generatePuzzle,
  generateForTarget,
  ruleShare,
  targetDistance,
  DifficultyTarget,
  increaseToUnique,
  randomizeRegions,
  reduceToUnique,
  regionsEqual,
  validateRegions,
  TRAINABLE_RULES,
  type ReduceResult,
  type SolutionInfo,
} from '../editor';
import { PracticeView } from './PracticeView';
import { RuleId } from '../../shared/rules';
import { BANDS, DifficultyBand, type PuzzleDifficulty } from '../../shared/analysis/difficulty';
import { RULE_NAMES } from '../../shared/analysis/rulemeta';

/** Total wall clock a targeted search may spend before settling for its closest miss. */
const GEN_BUDGET_MS = 25_000;
/**
 * How long one slice of that search runs. The sampler is synchronous, so this is also the
 * granularity at which the spinner and candidate count can repaint, and it wants to be small —
 * but each slice starts a fresh `generateForTarget`, so a slice too short to finish one
 * generate-and-profile cycle throws its work away. Measured: at 12×12 a 1.2s slice comes back
 * with no candidate half the time, a 3s slice does not; at 8–9×9 a 1.2s slice yields one or two.
 */
const genChunkMs = (size: number) => (size <= 9 ? 1_200 : 3_000);

interface Props {
  initialPuzzleId?: number; // import an existing puzzle as the base
  /** Technique to pre-select as the generator's practice target (from Tips & Tricks). */
  initialPractice?: RuleId;
  /** Route says "play the draft" — honoured only while a draft is actually in memory. */
  trying: boolean;
  /** Enter the temporary board (pushes a history entry, so back returns to the editor). */
  onTry: () => void;
  /** Leave it again. */
  onExitTry: () => void;
  /** A cold load of the ?try=1 URL has no draft to play — tell the router to drop the flag. */
  onTryUnavailable: () => void;
  onBack: () => void;
  onSaved: (id: number) => void;
}

type Tool = 'paint' | 'restrict' | 'mark';
/** Generating and hand-painting are separate jobs; the panel shows one at a time. */
type Mode = 'generate' | 'edit';
/** `kind` only picks the note's accent color: in progress / hit the target / settled for close. */
type GenNote = { text: string; kind: 'searching' | 'hit' | 'miss' };

export function EditorView({ initialPuzzleId, initialPractice, trying, onTry, onExitTry, onTryUnavailable, onBack, onSaved }: Props) {
  // Practising one technique means playing several boards in a row, so start those small: a 6×6
  // shows the deduction just as well and takes a fraction of the time to finish.
  const initialSize = initialPractice ? 6 : 8;
  const [size, setSize] = useState(initialSize);
  const [regions, setRegions] = useState<string[][]>(() => blankRegions(initialSize));
  const [activeColor, setActiveColor] = useState('A');
  // Opening a base puzzle means "edit this one", so land on the tools rather than the generator.
  const [mode, setMode] = useState<Mode>(initialPuzzleId === undefined ? 'generate' : 'edit');
  const [tool, setTool] = useState<Tool>('paint');
  const [restriction, setRestriction] = useState<Set<string>>(new Set());
  const [marks, setMarks] = useState<Set<string>>(new Set());
  const [showSolution, setShowSolution] = useState(false);
  const [solutionIdx, setSolutionIdx] = useState(0);
  const [info, setInfo] = useState<SolutionInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genBand, setGenBand] = useState<DifficultyBand | 'any'>('any');
  const [genTrains, setGenTrains] = useState<RuleId | 'any'>(initialPractice ?? 'any');
  const [genNote, setGenNote] = useState<GenNote | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [undoCount, setUndoCount] = useState(0);
  // The board handed to the practice game — a snapshot, so editing on afterwards (or hitting the
  // browser's back button into the editor) can't change the puzzle being played. `tryRun` restarts
  // the game each time "Play without saving" is pressed.
  const [tryRegions, setTryRegions] = useState<string[][] | null>(null);
  const [tryRun, setTryRun] = useState(0);
  const undoStack = useRef<Array<{ regions: string[][], restriction: Set<string> }>>([]);
  const dragTurnOn = useRef(false);
  const preDragRegions = useRef<string[][] | null>(null);
  const preDragRestriction = useRef<Set<string> | null>(null);
  const dragMadeChange = useRef(false);
  // Bumped to abandon an in-flight chunked search (a new press, or unmount).
  const genRun = useRef(0);

  // Import a base puzzle if requested.
  useEffect(() => {
    if (initialPuzzleId === undefined) return;
    api.getPuzzle(initialPuzzleId)
      .then(p => {
        setSize(p.size);
        setRegions(p.colorRegions.map(row => [...row]));
        // Select a color that actually exists on the imported board.
        const first = p.colorRegions.flat().find(c => COLOR_LABELS.includes(c));
        if (first) setActiveColor(first);
      })
      .catch(() => setLoadError(true));
  }, [initialPuzzleId]);

  useEffect(() => () => { genRun.current++; }, []);

  // Reloading /editor?try=1 lands here with nothing to play (the draft only ever lived in memory),
  // so the URL is corrected rather than left describing a board that isn't there.
  useEffect(() => {
    if (trying && !tryRegions) onTryUnavailable();
  }, [trying, tryRegions]);

  // Live solution count, debounced so painting stays responsive.
  useEffect(() => {
    setBusy(true);
    const t = setTimeout(() => {
      setInfo(countSolutions(regions));
      setBusy(false);
    }, 120);
    return () => clearTimeout(t);
  }, [regions]);

  const validity = useMemo(() => validateRegions(regions), [regions]);

  // Palette = the colors actually present on the board (so imported puzzles using
  // a sparse label set still show all their colors), padded with unused labels in
  // canonical order up to `size` so new colors can be introduced.
  const colors = useMemo(() => {
    const present = new Set<string>();
    for (const row of regions) for (const cell of row) present.add(cell);
    const ordered = COLOR_LABELS.filter(l => present.has(l));
    for (const l of COLOR_LABELS) {
      if (ordered.length >= size) break;
      if (!present.has(l)) ordered.push(l);
    }
    return ordered;
  }, [regions, size]);

  const solutions = info?.solutions ?? [];
  const safeIdx = solutions.length ? Math.min(solutionIdx, solutions.length - 1) : 0;
  const targetSolution = solutions[safeIdx];
  const queens = useMemo(() => {
    if (!showSolution || !targetSolution) return undefined;
    return new Set(targetSolution.map((col, row) => `${row},${col}`));
  }, [showSolution, targetSolution]);

  function cycleSolution(delta: number) {
    if (solutions.length === 0) return;
    setSolutionIdx((safeIdx + delta + solutions.length) % solutions.length);
  }

  function pushUndo() {
    undoStack.current.push({ regions, restriction });
    if (undoStack.current.length > 100) undoStack.current.shift();
    setUndoCount(undoStack.current.length);
  }

  function commitRegions(next: string[][]) {
    pushUndo();
    setRegions(next);
  }

  function changeSize(next: number) {
    const clamped = Math.max(MIN_SIZE, Math.min(MAX_SIZE, next));
    setSize(clamped);
    setRestriction(new Set());
    setMarks(new Set());
    undoStack.current = [];
    setUndoCount(0);
    setRegions(blankRegions(clamped));
    if (COLOR_LABELS.indexOf(activeColor) >= clamped) setActiveColor('A');
  }

  function handleCellEnter({ row, col }: { row: number; col: number }, isFirst: boolean) {
    if (mode !== 'edit') return; // the board is read-only on the generator tab
    // ✕ marks are a scratch overlay, deliberately outside the undo stack.
    if (tool === 'mark') {
      if (isFirst) dragTurnOn.current = !marks.has(`${row},${col}`);
      setMarks(prev => {
        const next = new Set(prev);
        if (dragTurnOn.current) next.add(`${row},${col}`); else next.delete(`${row},${col}`);
        return next;
      });
      return;
    }
    if (tool === 'restrict') {
      if (isFirst) {
        preDragRestriction.current = new Set(restriction);
        dragTurnOn.current = !restriction.has(`${row},${col}`);
      }
      setRestriction(prev => {
        const next = new Set(prev);
        if (dragTurnOn.current) next.add(`${row},${col}`); else next.delete(`${row},${col}`);
        return next;
      });
      return;
    }
    if (isFirst) {
      preDragRegions.current = regions;
      dragMadeChange.current = false;
    }
    setRegions(prev => {
      if (prev[row][col] === activeColor) return prev;
      if (restriction.has(`${row},${col}`)) return prev;
      dragMadeChange.current = true;
      const next = cloneRegions(prev);
      next[row][col] = activeColor;
      return next;
    });
  }

  function handleDragEnd() {
    if (dragMadeChange.current && preDragRegions.current !== null) {
      undoStack.current.push({ regions: preDragRegions.current, restriction });
      if (undoStack.current.length > 100) undoStack.current.shift();
      setUndoCount(undoStack.current.length);
    } else if (preDragRestriction.current !== null) {
      undoStack.current.push({ regions, restriction: preDragRestriction.current });
      if (undoStack.current.length > 100) undoStack.current.shift();
      setUndoCount(undoStack.current.length);
    }
    preDragRegions.current = null;
    preDragRestriction.current = null;
    dragMadeChange.current = false;
  }

  function handleDragAbort() {
    if (preDragRegions.current !== null) setRegions(preDragRegions.current);
    preDragRegions.current = null;
    if (preDragRestriction.current !== null) setRestriction(preDragRestriction.current);
    preDragRestriction.current = null;
    dragMadeChange.current = false;
  }

  function handleRestrictColor(color: string) {
    pushUndo();
    setRestriction(prev => {
      const keys = regions.flatMap((row, r) =>
        row.reduce<string[]>((acc, c, col) => { if (c === color) acc.push(`${r},${col}`); return acc; }, [])
      );
      const allLocked = keys.every(k => prev.has(k));
      const next = new Set(prev);
      if (allLocked) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      return next;
    });
  }

  function editablePredicate(): ((r: number, c: number) => boolean) | undefined {
    if (restriction.size === 0) return undefined;
    return (r, c) => !restriction.has(`${r},${c}`);
  }

  // One context-aware action: too many solutions ⇒ reduce; none ⇒ build one. Both
  // converge on exactly one solution.
  function handleCreateUnique() {
    setBusy(true);
    setTimeout(() => {
      const opts = {
        cap: DEFAULT_CAP,
        editable: editablePredicate(),
        target: showSolution ? targetSolution : undefined,
      };
      const result: ReduceResult = info?.count === 0
        ? increaseToUnique(regions, opts)
        : reduceToUnique(regions, opts);

      if (regionsEqual(result.regions, regions)) setInfo(countSolutions(result.regions));
      else commitRegions(result.regions);
      setBusy(false);

      if (result.unique) {
        setSaveError(null);
      } else if (result.reason === 'unsolvable') {
        setSaveError(`Couldn't build a solution${restriction.size ? ' within the selection' : ''}. Try a different size or clear the restriction.`);
      } else {
        setSaveError(`Stopped at ${result.solutionCount} solution${result.solutionCount === 1 ? '' : 's'}${restriction.size ? ' within the selection' : ''}. Try clearing the restriction or randomizing first.`);
      }
    }, 10);
  }

  function handleRandomize() {
    const next = randomizeRegions(regions, Math.max(2, Math.round(size / 2)), editablePredicate());
    commitRegions(next);
  }

  /**
   * Fresh random puzzle with a unique solution — replaces the whole board (unlike
   * randomize/reduce, which perturb it), so it ignores any edit restriction.
   *
   * The difficulty / practices selects narrow what counts as acceptable. Both on "any" is
   * one `generatePuzzle` call and lands immediately; anything else is rejection sampling —
   * keep drawing fresh puzzles until one hits the target.
   *
   * Rejection sampling, deliberately: measured hit rates are high enough (see `TRAINABLE_RULES`
   * for the technique targets; every difficulty band is 9–29% of fresh 8×8 generations) that a
   * guided search would be more machinery for a problem plain sampling solves in seconds.
   *
   * The sampler is synchronous, so the search runs in short `genChunkMs` slices with a timeout
   * between them: that is what lets the spinner and the candidate count actually paint
   * mid-search instead of after it.
   */
  function handleGenerate() {
    const target: DifficultyTarget = {};
    if (genBand !== 'any') target.band = genBand;
    const trainable = TRAINABLE_RULES.find(t => t.rule === genTrains);
    if (trainable) target.trains = { rule: trainable.rule, minShare: trainable.minShare };
    const targeted = genBand !== 'any' || genTrains !== 'any';

    const run = ++genRun.current;
    setGenerating(true);
    setSaveError(null);
    setGenNote(targeted ? { text: 'Searching…', kind: 'searching' } : null);

    if (!targeted) {
      // A single call of a few seconds with nothing to report along the way: the spinner is
      // the whole progress story.
      setTimeout(() => {
        const result = generatePuzzle(size);
        if (genRun.current !== run) return;
        if (result.regions) commitRegions(result.regions);
        else setSaveError('Generation failed to find a unique puzzle — try again.');
        setGenerating(false);
      }, 30);
      return;
    }

    const deadline = Date.now() + GEN_BUDGET_MS;
    let best: { regions: string[][]; profile: PuzzleDifficulty } | null = null;
    let bestD = Infinity;
    let candidates = 0;

    const step = () => {
      if (genRun.current !== run) return;
      const result = generateForTarget(size, target, { budgetMs: genChunkMs(size), maxCandidates: 2 });
      if (genRun.current !== run) return; // unmounted or superseded while the chunk ran

      candidates += result.candidates;
      if (result.regions && result.profile) {
        // Best-so-far is tracked here rather than inside the sampler, because each chunk only
        // knows about its own candidates.
        const d = targetDistance(result.profile, target);
        if (d < bestD) { bestD = d; best = { regions: result.regions, profile: result.profile }; }
      }

      if (bestD > 0 && Date.now() < deadline) {
        setGenNote({
          text: `Searching… ${candidates} candidate${candidates === 1 ? '' : 's'} so far`,
          kind: 'searching',
        });
        setTimeout(step, 16); // a frame for the count to paint before the next slice blocks
        return;
      }

      setGenerating(false);
      if (!best) {
        setGenNote(null);
        setSaveError('Generation failed to find a unique puzzle — try again.');
        return;
      }
      commitRegions(best.regions);

      const matched = bestD === 0;
      const secs = (best.profile.refSolveMs / 1000).toFixed(0);
      // Report the dimension that was actually targeted, not just the band — "closest match"
      // is useless feedback if it does not say close on what.
      const parts = [best.profile.band, `~${secs}s to solve`];
      if (genTrains !== 'any') {
        parts.push(`${(100 * ruleShare(best.profile, genTrains)).toFixed(0)}% ${RULE_NAMES[genTrains].toLowerCase()}`);
      }
      const tries = `${candidates} candidate${candidates === 1 ? '' : 's'}`;
      setGenNote({
        kind: matched ? 'hit' : 'miss',
        text: matched
          ? `✓ ${parts.join(' · ')} (${tries})`
          : `Closest of ${tries} · ${parts.join(' · ')}`,
      });
    };
    setTimeout(step, 30);
  }

  function handleUndo() {
    const prev = undoStack.current.pop();
    setUndoCount(undoStack.current.length);
    if (prev) {
      setRegions(prev.regions);
      setRestriction(prev.restriction);
    }
  }

  /** Try the draft out on a real board without publishing it (see `PracticeView`). */
  function handleTry() {
    setTryRegions(cloneRegions(regions));
    setTryRun(n => n + 1);
    onTry();
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      // No name field: the server numbers community puzzles in creation order.
      const summary = await api.saveUserPuzzle({
        colorRegions: regions,
        creatorToken: getPlayerToken(),
      });
      onSaved(summary.id);
    } catch (e) {
      setSaveError((e as Error).message || 'Failed to save');
      setSaving(false);
    }
  }

  // Solution-count badge styling.
  let countLabel = '…';
  let countClass = 'count-neutral';
  if (info) {
    if (info.count === 0) { countLabel = 'No solution'; countClass = 'count-bad'; }
    else if (info.count === 1) { countLabel = '1 — unique ✓'; countClass = 'count-good'; }
    else { countLabel = info.capped ? `${info.count}+ solutions` : `${info.count} solutions`; countClass = 'count-many'; }
  }

  // Only unique puzzles are publishable, so the save button gates on the count rather than
  // reporting it.
  const unique = info?.count === 1;
  const savable = unique && validity.brokenRegions.length === 0;
  // Playable is the weaker bar: any board with a solution can be tried, unique or not. A count of
  // 0 implies the colors don't work out either, since `solve` places one queen per region.
  const playable = (info?.count ?? 0) > 0;

  if (loadError) return <p className="error">Failed to load the base puzzle.</p>;

  // Playing the draft swaps out the editor's UI but leaves this component mounted — that is what
  // keeps the board, the tools, the undo stack and the generator selects waiting on return.
  if (trying && tryRegions) {
    return <PracticeView key={tryRun} regions={tryRegions} onExit={onExitTry} />;
  }

  return (
    <div id="view-editor">
      <div className="editor-topbar">
        <button className="btn-secondary" onClick={onBack}>← Back</button>
        <h1>Puzzle Editor</h1>
      </div>

      <div className="editor-layout">
        <EditorBoard
          regions={regions}
          readOnly={mode !== 'edit'}
          onCellEnter={handleCellEnter}
          onDragEnd={handleDragEnd}
          onDragAbort={handleDragAbort}
          onPickColor={mode !== 'edit' ? undefined : tool === 'paint' ? setActiveColor : tool === 'restrict' ? handleRestrictColor : undefined}
          selected={tool === 'restrict' || restriction.size > 0 ? restriction : undefined}
          queens={queens}
          marks={marks.size > 0 ? marks : undefined}
        />

        <div className="editor-panel">
          <div className={`solution-count ${countClass}`}>{busy ? 'Counting…' : countLabel}</div>

          <div className={`editor-validity ${validity.ok ? 'ok' : 'warn'}`}>
            {validity.colorCount} / {size} colors
            {validity.brokenRegions.length > 0 && ` · split regions: ${validity.brokenRegions.join(', ')}`}
            {validity.colorCount !== size && ` · need exactly ${size} colors`}
          </div>

          <div className="editor-row">
            Size
            <button className="btn-stepper" onClick={() => changeSize(size - 1)} disabled={busy || generating || size <= MIN_SIZE}>−</button>
            <span className="size-value">{size}</span>
            <button className="btn-stepper" onClick={() => changeSize(size + 1)} disabled={busy || generating || size >= MAX_SIZE}>+</button>
            <span className="editor-hint">(clears the board)</span>
          </div>

          <div className="editor-tabs">
            <button className={mode === 'generate' ? 'active' : ''} onClick={() => setMode('generate')}>🎲 Generate</button>
            <button className={mode === 'edit' ? 'active' : ''} onClick={() => setMode('edit')}>✏️ Edit by hand</button>
          </div>

          {mode === 'generate' ? (
            <div className="editor-section editor-generate">
              <div className="editor-generate-row">
                <label>
                  Difficulty
                  <select value={genBand} onChange={e => setGenBand(e.target.value as DifficultyBand | 'any')} disabled={generating}>
                    <option value="any">any</option>
                    {BANDS.map(b => <option key={b.band} value={b.band}>{b.band}</option>)}
                  </select>
                </label>
                <label>
                  Practices
                  <select value={genTrains} onChange={e => setGenTrains(e.target.value as RuleId | 'any')} disabled={generating}>
                    <option value="any">any technique</option>
                    {TRAINABLE_RULES.map(t => <option key={t.rule} value={t.rule}>{RULE_NAMES[t.rule]}</option>)}
                  </select>
                </label>
              </div>
              <button className="btn-primary btn-generate" onClick={handleGenerate} disabled={busy || generating}>
                {generating ? <><span className="spinner" />Generating…</> : <>🎲 Generate puzzle</>}
              </button>
              {genNote && <div className={`editor-generate-note ${genNote.kind}`}>{genNote.text}</div>}
              <p className="editor-hint">
                Both on “any” gives a random puzzle. Narrowing either samples until one matches.
              </p>
            </div>
          ) : (
            <>
              <div className="editor-section">
                <div className="editor-tools">
                  <button className={tool === 'paint' ? 'active' : ''} onClick={() => setTool('paint')}>Paint</button>
                  <button className={tool === 'restrict' ? 'active' : ''} onClick={() => setTool('restrict')}>
                    Restrict {restriction.size > 0 ? `(${restriction.size})` : ''}
                  </button>
                  <button className={tool === 'mark' ? 'active' : ''} onClick={() => setTool('mark')}>
                    Mark ✕ {marks.size > 0 ? `(${marks.size})` : ''}
                  </button>
                  <button className="btn-secondary" onClick={handleUndo} disabled={undoCount === 0}>Undo</button>
                </div>
                {tool === 'restrict' && (
                  <p className="editor-hint">
                    Drag to lock cells from painting or reduce/randomize.{' '}
                    <span className="hint-mouse">Right-click</span>
                    <span className="hint-touch">Long-press</span>
                    {' '}a cell to lock/unlock its whole color.
                    {restriction.size > 0 && <> <button className="link-btn" onClick={() => setRestriction(new Set())}>clear</button></>}
                  </p>
                )}
                {tool === 'mark' && (
                  <p className="editor-hint">
                    Drag to toggle ✕ marks — a visual aid while building, not saved with the puzzle.
                    {marks.size > 0 && <> <button className="link-btn" onClick={() => setMarks(new Set())}>clear</button></>}
                  </p>
                )}
                {tool === 'paint' && (
                  <p className="editor-hint">
                    Drag to paint.{' '}
                    <span className="hint-mouse">Right-click a cell to pick its color.</span>
                    <span className="hint-touch">Long-press a cell to pick its color.</span>
                  </p>
                )}
                <div className="editor-palette">
                  {colors.map(label => (
                    <button
                      key={label}
                      className={`swatch color-${COLOR_LABELS.indexOf(label)}${label === activeColor ? ' active' : ''}`}
                      onClick={() => { setActiveColor(label); setTool('paint'); }}
                      title={label}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="editor-section">
                <button className="btn-primary" onClick={handleCreateUnique} disabled={busy || !info || info.count === 1}>
                  {info?.count === 1 ? 'Unique ✓' : 'Create unique solution'}
                </button>
                <button className="btn-secondary" onClick={handleRandomize} disabled={busy}>Randomize regions</button>
              </div>
            </>
          )}

          <div className="editor-section">
            <label className="editor-row">
              <input type="checkbox" checked={showSolution} onChange={e => setShowSolution(e.target.checked)} />
              {solutions.length === 1 ? 'Preview solution' : 'Preview solutions'}
            </label>
            {/* Nothing to cycle through when there is one solution — and every saveable puzzle
                has exactly one, so this row is the exception rather than the rule. */}
            {showSolution && solutions.length > 1 && (
              <div className="editor-row solution-cycler">
                <button className="btn-secondary" onClick={() => cycleSolution(-1)} disabled={solutions.length < 2}>◀</button>
                <span>Solution {safeIdx + 1} / {info?.capped ? `${solutions.length}+` : solutions.length}</span>
                <button className="btn-secondary" onClick={() => cycleSolution(1)} disabled={solutions.length < 2}>▶</button>
              </div>
            )}
          </div>

          <div className="editor-section">
            {/* Trying the board out leads, and publishing follows it: playing is the cheaper,
                more common move, and the one you want before you inflict a puzzle on everyone. */}
            <div className="editor-finish">
              <button
                className="btn-primary"
                onClick={handleTry}
                disabled={busy || generating || saving || !playable}
                title={playable
                  ? 'Play this board now — nothing is saved, and it stays off your record'
                  : 'Needs a board with at least one solution'}
              >
                ▶ Play without saving
              </button>
              <button
                className="btn-secondary"
                onClick={handleSave}
                disabled={saving || busy || generating || !savable}
                title={validity.brokenRegions.length > 0 ? 'Fix split regions first' : !unique ? 'Only puzzles with exactly one solution can be saved' : ''}
              >
                {saving ? 'Publishing…' : 'Publish to community'}
              </button>
            </div>
            {info && !unique && (
              <p className="editor-hint">Only puzzles with exactly one solution can be saved.</p>
            )}
            {saveError && <p className="error">{saveError}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
